import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useTranslation } from 'react-i18next'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

const FramePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { frames, config: globalConfig, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.frame
  const { setSessionState } = useSessionStateStore()
  const [selectedFrame, setSelectedFrame] = useState<string | null>(frames[0]._id)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)
  const [remainingTime, setRemainingTime] = useState<string>('--')
  const welcomeSettings = getPageSettings('welcome')

  // Initialize countdown with the useCountdown hook
  const frameTimeout = config?.settings?.frameTimeout || 0
  const countdown = useCountdown(frameTimeout)

  useEffect(() => {
    if (frames.length > 0) {
      setSelectedFrame(frames[0]._id)
    }
  }, [frames])

  // Call all hooks at the top level
  const cachedFrameBg = useCachedBackgroundImage('/assets/photophoria/photophoria-frame.webp')
  const cachedFrameButton = useCachedImage('/assets/photophoria/photophoria-frame-button.webp')

  const handleNext = (): void => {
    if (!selectedFrame) return

    const frame = frames.find((f) => f._id === selectedFrame)
    if (!frame) return

    // Find first photo type image and get its ratio
    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        frameId: selectedFrame,
        framePhotoRatio: photoRatio
      },
      timeline: {
        activityLogs: [
          {
            page: 'frame',
            type: 'selection',
            action: 'complete',
            details: { frameId: selectedFrame },
            timestamp: new Date()
          }
        ]
      }
    })
    // Navigate based on startWithPayment setting
    const nextPage = welcomeSettings?.startWithPayment ? 'capture' : 'checkout'
    navigate(`/${projectId}/${nextPage}`)
  }

  // Set up countdown callbacks with dependencies on selectedFrame
  useEffect(() => {
    if (frameTimeout > 0) {
      // Set the onTick callback to update the remaining time display
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      // Set the onComplete callback that will use the latest state
      countdown.setOnComplete(() => {
        setSessionState({
          timeline: {
            activityLogs: [
              {
                page: 'frame',
                type: 'selection',
                action: 'timeout',
                details: {
                  frameId: selectedFrame,
                  timeoutDuration: frameTimeout
                },
                timestamp: new Date()
              }
            ]
          }
        })
        handleNext()
      })
    }
  }, [selectedFrame, frameTimeout, setSessionState])

  // Start the countdown on mount
  useEffect(() => {
    if (frameTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [frameTimeout])

  return (
    <div
      className="w-full h-full flex bg-cover bg-center"
      data-page="frame"
      style={{
        backgroundImage: `url(${cachedFrameBg})`
      }}
    >
      {/* Frame Selection */}
      <div className="flex-1 flex flex-col w-full max-w-[70%] pb-8 mr-24 mb-16">
        {/* Category Tabs - Only show if frameCategories exist and are not empty */}
        {config?.settings?.frameCategories && config.settings.frameCategories.length > 0 && (
          <div className="shadow-xl w-full hidden">
            <div className="overflow-x-auto w-full flex justify-left gap-4 px-12 py-4">
              {config?.settings?.frameCategories.map((category) => (
                <button
                  type="button"
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`px-8 py-3 rounded-full text-xl whitespace-nowrap transition-all
                    ${
                      selectedCategory === category.name
                        ? 'bg-veranda text-white'
                        : 'border border-carbon text-carbon'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Frames Grid */}
        <div
          className={`flex-1 overflow-y-auto pl-36 pr-4 mt-32 ${config?.settings?.frameCategories?.length ? 'py-12' : 'py-8'}`}
        >
          <div
            className={`grid ${isPotrait ? 'grid-cols-1' : 'grid-cols-3'} gap-4 items-end`}
            role="listbox"
            title={t('frame.framesListbox', 'Frames')}
          >
            {frames.map((frame) => (
              <button
                role="option"
                aria-label={frame.name}
                type="button"
                key={frame._id}
                onClick={() => setSelectedFrame(frame._id)}
                className={`cursor-pointer transition-all flex flex-col p-8 items-center
                  ${selectedFrame === frame._id ? 'scale-105' : ''}`}
              >
                <img
                  crossOrigin="anonymous"
                  src={frame.thumbnail}
                  alt={frame.name}
                  className={`w-full shadow-xl object-contain rounded-lg ${
                    selectedFrame === frame._id
                      ? 'border-4 border-veranda'
                      : 'border border-gray-100'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Frame Preview */}
      <div
        className={`flex flex-col items-center justify-center gap-36 mt-48 w-full ${isPotrait ? 'p-12 max-w-[40%]' : 'p-8 max-w-[32%]'}`}
      >
        <div className="w-[360px] px-4">
          <img
            src={frames.find((f) => f._id === selectedFrame)?.thumbnail || ''}
            alt={t('frame.previewAlt', 'Preview')}
            crossOrigin="anonymous"
            className="w-full object-contain drop-shadow-xl"
          />
        </div>

        <div className="flex flex-col items-center gap-4">
          {(config?.settings?.frameTimeout || 0) > 0 && (
            <span className="text-2xl text-carbon">{remainingTime}</span>
          )}
          {/* Next Button */}
          <button onClick={handleNext}>
            <img
              src={cachedFrameButton}
              alt={t('common.next', 'Next')}
              className="w-60 h-auto transition-transform transform hover:scale-105"
            />
          </button>
        </div>
      </div>
    </div>
  )
}

export default FramePage
